// src/PhantomClient.ts
import {
  AddUserToOrganizationMethodEnum,
  Configuration,
  CreateAuthenticatorMethodEnum,
  CreateOrganizationMethodEnum,
  CreateWalletMethodEnum,
  DeleteAuthenticatorMethodEnum,
  GetAccountsMethodEnum,
  GrantOrganizationAccessMethodEnum,
  KMSRPCApi,
  SignTransactionMethodEnum,
  SignUTF8MessageMethodEnum,
  UserPolicyOneOfTypeEnum
} from "@phantom/openapi-wallet-service";
import axios from "axios";
import { Buffer } from "buffer";

// src/caip2-mappings.ts
import { NetworkId } from "@phantom/constants";
var CAIP2_NETWORK_MAPPINGS = {
  // Solana networks
  [NetworkId.SOLANA_MAINNET]: {
    chain: "solana",
    network: "mainnet",
    description: "Solana Mainnet-Beta"
  },
  [NetworkId.SOLANA_DEVNET]: {
    chain: "solana",
    network: "devnet",
    description: "Solana Devnet"
  },
  [NetworkId.SOLANA_TESTNET]: {
    chain: "solana",
    network: "testnet",
    description: "Solana Testnet"
  },
  // Ethereum/EVM networks
  [NetworkId.ETHEREUM_MAINNET]: {
    chain: "ethereum",
    network: "mainnet",
    description: "Ethereum Mainnet"
  },
  [NetworkId.ETHEREUM_SEPOLIA]: {
    chain: "ethereum",
    network: "sepolia",
    description: "Sepolia Testnet"
  },
  [NetworkId.POLYGON_MAINNET]: {
    chain: "polygon",
    network: "mainnet",
    description: "Polygon Mainnet"
  },
  [NetworkId.POLYGON_AMOY]: {
    chain: "polygon",
    network: "amoy",
    description: "Polygon Amoy Testnet"
  },
  [NetworkId.BASE_MAINNET]: {
    chain: "base",
    network: "mainnet",
    description: "Base Mainnet"
  },
  [NetworkId.BASE_SEPOLIA]: {
    chain: "base",
    network: "sepolia",
    description: "Base Sepolia Testnet"
  },
  [NetworkId.ARBITRUM_ONE]: {
    chain: "arbitrum",
    network: "mainnet",
    description: "Arbitrum One"
  },
  [NetworkId.ARBITRUM_SEPOLIA]: {
    chain: "arbitrum",
    network: "sepolia",
    description: "Arbitrum Sepolia Testnet"
  },
  [NetworkId.MONAD_MAINNET]: {
    chain: "monad",
    network: "mainnet",
    description: "Monad Mainnet"
  },
  [NetworkId.MONAD_TESTNET]: {
    chain: "monad",
    network: "testnet",
    description: "Monad Testnet"
  },
  // Bitcoin networks (for future support)
  [NetworkId.BITCOIN_MAINNET]: {
    chain: "bitcoin",
    network: "mainnet",
    description: "Bitcoin Mainnet"
  },
  [NetworkId.BITCOIN_TESTNET]: {
    chain: "bitcoin",
    network: "testnet",
    description: "Bitcoin Testnet"
  },
  // Sui networks (for future support)
  [NetworkId.SUI_MAINNET]: {
    chain: "sui",
    network: "mainnet",
    description: "Sui Mainnet"
  },
  [NetworkId.SUI_TESTNET]: {
    chain: "sui",
    network: "testnet",
    description: "Sui Testnet"
  }
};
function deriveSubmissionConfig(networkId) {
  const mapping = CAIP2_NETWORK_MAPPINGS[networkId];
  if (!mapping) {
    return void 0;
  }
  return {
    chain: mapping.chain,
    network: mapping.network
  };
}
function supportsTransactionSubmission(networkId) {
  return networkId in CAIP2_NETWORK_MAPPINGS;
}
function getNetworkDescription(networkId) {
  return CAIP2_NETWORK_MAPPINGS[networkId]?.description;
}
function getSupportedNetworkIds() {
  return Object.keys(CAIP2_NETWORK_MAPPINGS);
}
function getNetworkIdsByChain(chain) {
  return Object.entries(CAIP2_NETWORK_MAPPINGS).filter(([_, mapping]) => mapping.chain.toLowerCase() === chain.toLowerCase()).map(([networkId]) => networkId);
}

// src/constants.ts
import { DerivationInfoCurveEnum, DerivationInfoAddressFormatEnum, Algorithm } from "@phantom/openapi-wallet-service";
var DerivationPath = {
  // Solana - BIP44 standard for Solana (coin type 501)
  Solana: (accountIndex = 0) => `m/44'/501'/${accountIndex}'/0'`,
  // Ethereum - BIP44 standard for Ethereum and all EVM-compatible chains (coin type 60)
  Ethereum: (accountIndex = 0) => `m/44'/60'/0'/0/${accountIndex}`,
  // Bitcoin - BIP84 standard for Bitcoin (coin type 0)
  Bitcoin: (accountIndex = 0) => `m/84'/0'/${accountIndex}'/0`,
  // Sui - BIP44 standard for Sui (coin type 784)
  Sui: (accountIndex = 0) => `m/44'/784'/${accountIndex}'/0'/0'`
};
function getDerivationPathForNetwork(networkId, accountIndex = 0) {
  const network = networkId.split(":")[0].toLowerCase();
  switch (network) {
    case "solana":
      return DerivationPath.Solana(accountIndex);
    case "sui":
      return DerivationPath.Sui(accountIndex);
    case "bitcoin":
    case "btc":
      return DerivationPath.Bitcoin(accountIndex);
    case "eip155":
    case "ethereum":
    case "eth":
    default:
      return DerivationPath.Ethereum(accountIndex);
  }
}
function getNetworkConfig(networkId, accountIndex = 0) {
  const network = networkId.split(":")[0].toLowerCase();
  switch (network) {
    case "solana":
      return {
        derivationPath: DerivationPath.Solana(accountIndex),
        curve: DerivationInfoCurveEnum.ed25519,
        algorithm: Algorithm.ed25519,
        addressFormat: DerivationInfoAddressFormatEnum.solana
      };
    case "sui":
      return {
        derivationPath: DerivationPath.Sui(accountIndex),
        curve: DerivationInfoCurveEnum.ed25519,
        algorithm: Algorithm.ed25519,
        addressFormat: DerivationInfoAddressFormatEnum.sui
      };
    case "bitcoin":
    case "btc":
      return {
        derivationPath: DerivationPath.Bitcoin(accountIndex),
        curve: DerivationInfoCurveEnum.secp256k1,
        algorithm: Algorithm.secp256k1,
        addressFormat: DerivationInfoAddressFormatEnum.bitcoinSegwit
        // Bitcoin uses a different format, but for SDK consistency we use Ethereum format
      };
    case "eip155":
      return {
        derivationPath: DerivationPath.Ethereum(accountIndex),
        curve: DerivationInfoCurveEnum.secp256k1,
        algorithm: Algorithm.secp256k1,
        addressFormat: DerivationInfoAddressFormatEnum.ethereum
        // EVM chains use Ethereum address format
      };
    default:
      return null;
  }
}

// src/errors.ts
import { isAxiosError } from "axios";
var WalletServiceError = class extends Error {
  constructor(data, errorName) {
    super(data.detail);
    this.name = errorName;
    this.type = data.type;
    this.title = data.title;
    this.detail = data.detail;
    this.requestId = data.requestId;
  }
};
function getAxiosErrorData(error) {
  if (isAxiosError(error)) {
    return error.response?.data;
  }
  return void 0;
}
function getErrorMessage(error, fallbackMessage = "An error occurred") {
  if (error instanceof WalletServiceError) {
    return error.detail || error.title || error.message || fallbackMessage;
  }
  const data = getAxiosErrorData(error);
  if (data) {
    return data.message || data.detail || data.title || fallbackMessage;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallbackMessage;
}
function parseWalletServiceError(data) {
  if (!data || !data.type) {
    return null;
  }
  switch (data.type) {
    case "spending-limit-exceeded":
      return new SpendingLimitError(data);
    case "transaction-blocked":
      return new TransactionBlockedError(data);
    default:
      return null;
  }
}
var SpendingLimitError = class extends WalletServiceError {
  constructor(data) {
    super(data, "SpendingLimitError");
    this.previousSpendCents = data.previousSpendCents;
    this.transactionSpendCents = data.transactionSpendCents;
    this.totalSpendCents = data.totalSpendCents;
    this.limitCents = data.limitCents;
  }
};
var TransactionBlockedError = class extends WalletServiceError {
  constructor(data) {
    super(data, "TransactionBlockedError");
    this.scannerResult = data.scannerResult;
  }
};

// src/PhantomClient.ts
import { getSecureTimestamp, randomUUID, isEthereumChain, isSolanaChain } from "@phantom/utils";
var PhantomClient = class {
  constructor(config, stamper) {
    this.config = {
      ...config,
      walletType: config.walletType || "user-wallet"
    };
    this.axiosInstance = axios.create();
    const customHeaders = {};
    if (config.headers) {
      Object.assign(customHeaders, config.headers);
    }
    if (Object.keys(customHeaders).length > 0) {
      this.axiosInstance.interceptors.request.use((config2) => {
        Object.assign(config2.headers, customHeaders);
        return config2;
      });
    }
    if (stamper) {
      this.axiosInstance.interceptors.request.use(async (config2) => {
        return await this.stampRequest(config2, stamper);
      });
      this.stamper = stamper;
    }
    const configuration = new Configuration({
      basePath: config.apiBaseUrl
    });
    this.kmsApi = new KMSRPCApi(configuration, config.apiBaseUrl, this.axiosInstance);
  }
  setOrganizationId(organizationId) {
    if (!organizationId) {
      throw new Error("organizationId is required");
    }
    this.config.organizationId = organizationId;
  }
  async createWallet(walletName) {
    try {
      if (!this.config.organizationId) {
        throw new Error("organizationId is required to create a wallet");
      }
      const timestamp = await getSecureTimestamp();
      const walletRequest = {
        organizationId: this.config.organizationId,
        walletName: walletName || `Wallet ${timestamp}`,
        accounts: [
          DerivationPath.Solana(),
          DerivationPath.Ethereum(),
          DerivationPath.Bitcoin(),
          DerivationPath.Sui()
        ]
      };
      const request = {
        method: CreateWalletMethodEnum.createWallet,
        params: walletRequest,
        timestampMs: timestamp
      };
      const response = await this.kmsApi.postKmsRpc(request);
      const walletResult = response.data.result;
      const requestAccounts = {
        method: GetAccountsMethodEnum.getAccounts,
        params: {
          accounts: [
            DerivationPath.Solana(),
            DerivationPath.Ethereum(),
            DerivationPath.Bitcoin(),
            DerivationPath.Sui()
          ],
          organizationId: this.config.organizationId,
          walletId: walletResult.walletId
        },
        timestampMs: timestamp
      };
      const accountsResponse = await this.kmsApi.postKmsRpc(requestAccounts);
      const accountsResult = accountsResponse.data.result;
      return {
        walletId: walletResult.walletId,
        addresses: accountsResult.map((account) => ({
          addressType: account.addressFormat,
          address: account.address
        }))
      };
    } catch (error) {
      console.error("Failed to create wallet:", error.response?.data || error.message);
      throw new Error(`Failed to create wallet: ${error.response?.data?.message || error.message}`);
    }
  }
  async prepare(transaction, organizationId, submissionConfig, account, methodName) {
    try {
      const request = {
        transaction,
        organizationId,
        submissionConfig,
        simulationConfig: { account }
      };
      const response = await this.axiosInstance.post(`${this.config.apiBaseUrl}/prepare`, request, {
        headers: {
          "Content-Type": "application/json",
          "X-Rpc-Method": methodName
        }
      });
      return response.data;
    } catch (error) {
      const errorData = getAxiosErrorData(error);
      const walletServiceError = parseWalletServiceError(errorData);
      if (walletServiceError) {
        throw walletServiceError;
      }
      const message = errorData?.detail || getErrorMessage(error, "Failed to submit transaction");
      throw new Error(message);
    }
  }
  async getTransactionForSigning(params) {
    const { encodedTransaction, networkId, submissionConfig, account } = params;
    const isEvmTransaction = isEthereumChain(networkId);
    const isSolanaTransaction = isSolanaChain(networkId);
    if (isEvmTransaction) {
      return { kind: "RLP_ENCODED", bytes: encodedTransaction };
    }
    if (isSolanaTransaction && this.config.walletType === "user-wallet") {
      if (!account) {
        throw new Error("Account is required to simulate Solana transactions with spending limits");
      }
      try {
        const prepareResponse = await this.prepare(
          encodedTransaction,
          this.config.organizationId,
          submissionConfig,
          account,
          params.methodName
        );
        return prepareResponse.transaction;
      } catch (e) {
        if (e instanceof WalletServiceError) {
          throw e;
        }
        const errorMessage = e instanceof Error ? e.message : String(e);
        throw new Error(errorMessage);
      }
    }
    return encodedTransaction;
  }
  getRpcMethodName(networkId, includeSubmissionConfig) {
    const isEvm = isEthereumChain(networkId);
    if (isEvm) {
      return includeSubmissionConfig ? "eth_sendTransaction" : "eth_signTransaction";
    }
    return includeSubmissionConfig ? "signAndSendTransaction" : "signTransaction";
  }
  /**
   * Private method for shared signing logic
   */
  async performTransactionSigning(params, includeSubmissionConfig) {
    const walletId = params.walletId;
    const encodedTransaction = params.transaction;
    const networkIdParam = params.networkId;
    const derivationIndex = params.derivationIndex ?? 0;
    const methodName = this.getRpcMethodName(networkIdParam, includeSubmissionConfig);
    try {
      if (!this.config.organizationId) {
        throw new Error("organizationId is required to sign a transaction");
      }
      const submissionConfig = deriveSubmissionConfig(networkIdParam);
      if (!submissionConfig) {
        throw new Error(`SubmissionConfig could not be derived for network ID: ${networkIdParam}`);
      }
      const networkConfig = getNetworkConfig(networkIdParam, derivationIndex);
      if (!networkConfig) {
        throw new Error(`Unsupported network ID: ${networkIdParam}`);
      }
      const derivationInfo = {
        derivationPath: networkConfig.derivationPath,
        curve: networkConfig.curve,
        addressFormat: networkConfig.addressFormat
      };
      const transactionForSigning = await this.getTransactionForSigning({
        encodedTransaction,
        networkId: networkIdParam,
        submissionConfig,
        account: params.account,
        methodName
      });
      const signRequest = {
        organizationId: this.config.organizationId,
        walletId,
        transaction: transactionForSigning,
        derivationInfo
      };
      if (includeSubmissionConfig) {
        signRequest.submissionConfig = submissionConfig;
      }
      if (includeSubmissionConfig && params.account) {
        signRequest.simulationConfig = {
          account: params.account
        };
      }
      const request = {
        method: SignTransactionMethodEnum.signTransaction,
        params: signRequest,
        timestampMs: await getSecureTimestamp()
      };
      const response = await this.kmsApi.postKmsRpc(request, {
        headers: {
          "X-Rpc-Method": methodName
        }
      });
      const result = response.data.result;
      const rpcSubmissionResult = response.data["rpc_submission_result"];
      const hash = includeSubmissionConfig && rpcSubmissionResult ? rpcSubmissionResult.result : null;
      return {
        signedTransaction: result.transaction,
        // Base64 encoded signed transaction
        hash
      };
    } catch (error) {
      const actionType = includeSubmissionConfig ? "sign and send" : "sign";
      if (error instanceof WalletServiceError) {
        throw error;
      }
      throw new Error(getErrorMessage(error, `Failed to ${actionType} transaction`));
    }
  }
  /**
   * Sign a transaction
   */
  async signTransaction(params) {
    const result = await this.performTransactionSigning(params, false);
    return {
      rawTransaction: result.signedTransaction
    };
  }
  /**
   * Sign and send a transaction
   */
  async signAndSendTransaction(params) {
    const result = await this.performTransactionSigning(params, true);
    return {
      rawTransaction: result.signedTransaction,
      hash: result.hash
    };
  }
  async getWalletAddresses(walletId, derivationPaths, derivationIndex) {
    try {
      const accountIndex = derivationIndex ?? 0;
      const paths = derivationPaths || [
        DerivationPath.Solana(accountIndex),
        DerivationPath.Ethereum(accountIndex),
        DerivationPath.Bitcoin(accountIndex),
        DerivationPath.Sui(accountIndex)
      ];
      const requestAccounts = {
        method: GetAccountsMethodEnum.getAccounts,
        params: {
          accounts: paths,
          organizationId: this.config.organizationId,
          walletId
        },
        timestampMs: await getSecureTimestamp()
      };
      const accountsResponse = await this.kmsApi.postKmsRpc(requestAccounts);
      const accountsResult = accountsResponse.data.result;
      const addresses = accountsResult.map((account) => ({
        addressType: account.addressFormat,
        address: account.address
      }));
      return addresses;
    } catch (error) {
      console.error("Failed to get wallet addresses:", error.response?.data || error.message);
      throw new Error(`Failed to get wallet addresses: ${error.response?.data?.message || error.message}`);
    }
  }
  /**
   * Sign an Ethereum message using EIP-191 personal sign
   */
  async ethereumSignMessage(params) {
    const walletId = params.walletId;
    const messageParam = params.message;
    const networkIdParam = params.networkId;
    const derivationIndex = params.derivationIndex ?? 0;
    try {
      if (!this.config.organizationId) {
        throw new Error("organizationId is required to sign a message");
      }
      const networkConfig = getNetworkConfig(networkIdParam, derivationIndex);
      if (!networkConfig) {
        throw new Error(`Unsupported network ID: ${networkIdParam}`);
      }
      const derivationInfo = {
        derivationPath: networkConfig.derivationPath,
        curve: networkConfig.curve,
        addressFormat: networkConfig.addressFormat
      };
      const base64StringMessage = messageParam;
      const request = {
        method: "ethereumSignMessage",
        params: {
          message: base64StringMessage,
          organizationId: this.config.organizationId,
          walletId,
          derivationInfo
        },
        timestampMs: await getSecureTimestamp()
      };
      const response = await this.kmsApi.postKmsRpc(request);
      const result = response.data.result;
      return result.signature;
    } catch (error) {
      console.error("Failed to sign Ethereum message:", error.response?.data || error.message);
      throw new Error(`Failed to sign Ethereum message: ${error.response?.data?.message || error.message}`);
    }
  }
  /**
   * Sign a UTF-8 message for Solana
   */
  async signUtf8Message(params) {
    const walletId = params.walletId;
    const messageParam = params.message;
    const networkIdParam = params.networkId;
    const derivationIndex = params.derivationIndex ?? 0;
    try {
      if (!this.config.organizationId) {
        throw new Error("organizationId is required to sign a message");
      }
      const networkConfig = getNetworkConfig(networkIdParam, derivationIndex);
      if (!networkConfig) {
        throw new Error(`Unsupported network ID: ${networkIdParam}`);
      }
      const derivationInfo = {
        derivationPath: networkConfig.derivationPath,
        curve: networkConfig.curve,
        addressFormat: networkConfig.addressFormat
      };
      const signRequest = {
        organizationId: this.config.organizationId,
        walletId,
        message: messageParam,
        algorithm: networkConfig.algorithm,
        derivationInfo
      };
      const request = {
        method: SignUTF8MessageMethodEnum.signUtf8Message,
        params: signRequest,
        timestampMs: await getSecureTimestamp()
      };
      const response = await this.kmsApi.postKmsRpc(request);
      const result = response.data.result;
      return result.signature;
    } catch (error) {
      console.error("Failed to sign raw payload:", error.response?.data || error.message);
      throw new Error(`Failed to sign raw payload: ${error.response?.data?.message || error.message}`);
    }
  }
  /**
   * Sign EIP-712 typed data for Ethereum
   */
  async ethereumSignTypedData(params) {
    const walletId = params.walletId;
    const typedData = params.typedData;
    const networkIdParam = params.networkId;
    const derivationIndex = params.derivationIndex ?? 0;
    try {
      if (!this.config.organizationId) {
        throw new Error("organizationId is required to sign typed data");
      }
      const networkConfig = getNetworkConfig(networkIdParam, derivationIndex);
      if (!networkConfig) {
        throw new Error(`Unsupported network ID: ${networkIdParam}`);
      }
      const derivationInfo = {
        derivationPath: networkConfig.derivationPath,
        curve: networkConfig.curve,
        addressFormat: networkConfig.addressFormat
      };
      const request = {
        method: "ethereumSignTypedData",
        params: {
          typedData,
          organizationId: this.config.organizationId,
          walletId,
          derivationInfo
        },
        timestampMs: await getSecureTimestamp()
      };
      const response = await this.kmsApi.postKmsRpc(request);
      const result = response.data.result;
      return result.signature;
    } catch (error) {
      console.error("Failed to sign typed data:", error.response?.data || error.message);
      throw new Error(`Failed to sign typed data: ${error.response?.data?.message || error.message}`);
    }
  }
  async getWallets(limit, offset) {
    try {
      const request = {
        method: "getOrganizationWallets",
        params: {
          organizationId: this.config.organizationId,
          limit: limit || 20,
          offset: offset || 0
        },
        timestampMs: await getSecureTimestamp()
      };
      const response = await this.kmsApi.postKmsRpc(request);
      const result = response.data.result;
      return {
        wallets: result.wallets.map((wallet) => ({
          walletId: wallet.walletId,
          walletName: wallet.walletName
        })),
        totalCount: result.totalCount,
        limit: result.limit,
        offset: result.offset
      };
    } catch (error) {
      console.error("Failed to get wallets:", error.response?.data || error.message);
      throw new Error(`Failed to get wallets: ${error.response?.data?.message || error.message}`);
    }
  }
  /**
   * Get organization details by organization ID
   */
  async getOrganization(organizationId) {
    try {
      const request = {
        method: "getOrganization",
        params: {
          organizationId
        },
        timestampMs: await getSecureTimestamp()
      };
      const response = await this.kmsApi.postKmsRpc(request);
      const result = response.data.result;
      return result;
    } catch (error) {
      console.error("Failed to get organization:", error.response?.data || error.message);
      throw new Error(`Failed to get organization: ${error.response?.data?.message || error.message}`);
    }
  }
  /**
   * Create a new organization with the specified name and users
   * @param name Organization name
   * @param users Array of users with their authenticators
   */
  validateNameLength(name, type) {
    const MAX_NAME_LENGTH = 64;
    if (name.length > MAX_NAME_LENGTH) {
      throw new Error(`${type} name cannot exceed ${MAX_NAME_LENGTH} characters. Current length: ${name.length}`);
    }
  }
  async createOrganization(name, users, tags) {
    try {
      if (!name) {
        throw new Error("Organization name is required");
      }
      this.validateNameLength(name, "Organization");
      if (!users || users.length === 0) {
        throw new Error("At least one user is required");
      }
      for (const user of users) {
        if (user.username) {
          this.validateNameLength(user.username, "Username");
        }
        for (const auth of user.authenticators) {
          if (auth.authenticatorName) {
            this.validateNameLength(auth.authenticatorName, "Authenticator");
          }
        }
      }
      const params = {
        organizationName: name,
        users: users.map((userConfig) => ({
          username: userConfig.username || `user-${randomUUID()}`,
          authenticators: userConfig.authenticators,
          policy: userConfig.role === "ADMIN" ? {
            type: UserPolicyOneOfTypeEnum.root
          } : { type: "CEL", preset: "LEGACY_USER_ROLE" }
        })),
        tags
      };
      const request = {
        method: CreateOrganizationMethodEnum.createOrganization,
        params,
        timestampMs: await getSecureTimestamp()
      };
      const response = await this.kmsApi.postKmsRpc(request);
      const result = response.data.result;
      return result;
    } catch (error) {
      console.error("Failed to create organization:", error.response?.data || error.message);
      throw new Error(`Failed to create organization: ${error.response?.data?.message || error.message}`);
    }
  }
  /**
   * Create an authenticator for a user in an organization
   */
  async createAuthenticator(params) {
    try {
      if (params.username) {
        this.validateNameLength(params.username, "Username");
      }
      if (params.authenticatorName) {
        this.validateNameLength(params.authenticatorName, "Authenticator");
      }
      if (params.authenticator?.authenticatorName) {
        this.validateNameLength(params.authenticator.authenticatorName, "Authenticator");
      }
      const requestParams = {
        organizationId: params.organizationId,
        username: params.username,
        authenticatorName: params.authenticatorName,
        authenticator: params.authenticator,
        replaceExpirable: params.replaceExpirable
      };
      const request = {
        method: CreateAuthenticatorMethodEnum.createAuthenticator,
        params: requestParams,
        timestampMs: await getSecureTimestamp()
      };
      const response = await this.kmsApi.postKmsRpc(request);
      const result = response.data.result;
      return result;
    } catch (error) {
      console.error("Failed to create authenticator:", error.response?.data || error.message);
      throw new Error(`Failed to create authenticator: ${error.response?.data?.message || error.message}`);
    }
  }
  /**
   * Delete an authenticator for a user in an organization
   */
  async deleteAuthenticator(params) {
    try {
      const requestParams = {
        organizationId: params.organizationId,
        username: params.username,
        authenticatorId: params.authenticatorId
      };
      const request = {
        method: DeleteAuthenticatorMethodEnum.deleteAuthenticator,
        params: requestParams,
        timestampMs: await getSecureTimestamp()
      };
      const response = await this.kmsApi.postKmsRpc(request);
      const result = response.data.result;
      return result;
    } catch (error) {
      console.error("Failed to delete authenticator:", error.response?.data || error.message);
      throw new Error(`Failed to delete authenticator: ${error.response?.data?.message || error.message}`);
    }
  }
  async grantOrganizationAccess(params) {
    try {
      const request = {
        method: GrantOrganizationAccessMethodEnum.grantOrganizationAccess,
        params,
        timestampMs: await getSecureTimestamp()
      };
      const response = await this.kmsApi.postKmsRpc(request);
      const result = response.data.result;
      return result;
    } catch (error) {
      console.error("Failed to grant organization access:", error.response?.data || error.message);
      throw new Error(`Failed to grant organization access: ${error.response?.data?.message || error.message}`);
    }
  }
  /**
   * Add a new user to an organization
   */
  async addUserToOrganization(params) {
    try {
      const request = {
        method: AddUserToOrganizationMethodEnum.addUserToOrganization,
        params,
        timestampMs: await getSecureTimestamp()
      };
      await this.kmsApi.postKmsRpc(request);
    } catch (error) {
      console.error("Failed to add user to organization:", error.response?.data || error.message);
      throw new Error(`Failed to add user to organization: ${error.response?.data?.message || error.message}`);
    }
  }
  /**
   * Get a wallet by tag from the specified organization
   */
  async getWalletWithTag(params) {
    try {
      const request = {
        method: "getWalletWithTag",
        params: {
          organizationId: params.organizationId,
          tag: params.tag,
          derivationPaths: params.derivationPaths
        },
        timestampMs: await getSecureTimestamp()
      };
      const response = await this.kmsApi.postKmsRpc(request);
      const result = response.data.result;
      return result;
    } catch (error) {
      console.error("Failed to get wallet with tag:", error.response?.data || error.message);
      throw new Error(`Failed to get wallet with tag: ${error.response?.data?.message || error.message}`);
    }
  }
  /**
   * Stamp an axios request with the provided stamper
   */
  async stampRequest(config, stamper) {
    const requestBody = typeof config.data === "string" ? config.data : config.data === void 0 ? "" : JSON.stringify(config.data);
    const dataUtf8 = Buffer.from(requestBody, "utf8");
    const stamp = await stamper.stamp({
      data: dataUtf8
    });
    config.headers = config.headers || {};
    config.headers["X-Phantom-Stamp"] = stamp;
    return config;
  }
};

// src/index.ts
import { generateKeyPair } from "@phantom/crypto";
import { NetworkId as NetworkId2 } from "@phantom/constants";
import {
  DerivationInfoAddressFormatEnum as DerivationInfoAddressFormatEnum2
} from "@phantom/openapi-wallet-service";
export {
  DerivationInfoAddressFormatEnum2 as AddressType,
  DerivationPath,
  NetworkId2 as NetworkId,
  PhantomClient,
  SpendingLimitError,
  TransactionBlockedError,
  WalletServiceError,
  deriveSubmissionConfig,
  generateKeyPair,
  getAxiosErrorData,
  getDerivationPathForNetwork,
  getErrorMessage,
  getNetworkConfig,
  getNetworkDescription,
  getNetworkIdsByChain,
  getSupportedNetworkIds,
  parseWalletServiceError,
  supportsTransactionSubmission
};
